import { ErrorOutline, AccessTime, Phone, ArrowForward, Voicemail, Email, PlayArrow, Pause, Replay10, Forward10, Speed, Download, VoicemailOutlined, ArrowForward as ArrowForwardIcon, Chat, Mail, Person } from "@mui/icons-material";
import { Button, CircularProgress, Menu, MenuItem, Slider, Tooltip } from "@mui/material";
import axios from "axios";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { formatTimestampForZone } from '../utils/formattimestamp';
import { formatPhoneNumber } from '../utils/formatnumber';
import { getActionDetailsLabel } from '../utils/actionDetailsLabel';
import ChangeOrgModalNew from "../modals/ChangeOrgModalNew";
import AudioPlayer from "../utils/AudioPlayer";
import { createPdfBlobFromTranscript } from "../utils/pdf_export";
import { ArrowLeft, AlertCircle, Volume2, Settings2, MailIcon } from "lucide-react";

// ElevenLabs Icon Component with fallback
const ElevenLabsIcon = ({ className = "w-4 h-4" }) => {
  const [imgError, setImgError] = useState(false);
  
  if (imgError) {
    return (
      <div className={`${className} bg-indigo-500 rounded flex items-center justify-center text-white text-xs font-bold`}>
        11
      </div>
    );
  }
  
  return (
    <img
      src="/icons/elevenlabs.svg"
      alt="Eleven Labs"
      className={className}
      onError={() => setImgError(true)}
    />
  );
};

// Helper function to render action with icon and label
const renderAction = (action) => {
  if (!action || action === 'N/A') {
    return <span className="text-gray-500">N/A</span>;
  }
  
  switch (action) {
    case 'play_recording':
      return (
        <div className="flex items-center">
          <Volume2 className="w-4 h-4 text-yellow-500" /> 
          <span className="ml-2">Play Recording</span>
        </div>
      );
    case 'forward':
      return (
        <div className="flex items-center">
          <ArrowForwardIcon sx={{ fontSize: '16px', color: '#10B981' }} /> 
          <span className="ml-2">Forwarded</span>
        </div>
      );
    case 'forwardvwm':
      return (
        <div className="flex items-center">
          <ArrowForwardIcon sx={{ fontSize: '16px', color: '#10B981' }} /> 
          <span className="ml-2">Forwarded w/VM</span>
        </div>
      );
    case 'dynamic_forward':
      return (
        <div className="flex items-center">
          <ArrowForwardIcon sx={{ fontSize: '16px', color: '#10B981' }} /> 
          <span className="ml-2">Dynamic Forward</span>
        </div>
      );
    case 'send_to_vm':
      return (
        <div className="flex items-center">
          <VoicemailOutlined sx={{ fontSize: '16px', color: '#6366F1' }} /> 
          <span className="ml-2">Sent to VM</span>
        </div>
      );
    case 'ai_agent':
    case 'ai_agent':
      return (
        <div className="flex items-center">
          <ElevenLabsIcon className="w-4 h-4" />
          <span className="ml-2">AI Agent</span>
        </div>
      );
    case 'ivr':
      return (
        <div className="flex items-center">
          <Settings2 className="w-4 h-4 text-indigo-500" /> 
          <span className="ml-2">Call Menu</span>
        </div>
      );
    case 'Outbound Call':
      return (
        <div className="flex items-center">
          <Phone className="w-4 h-4 text-green-500" /> 
          <span className="ml-2">Outbound Call</span>
        </div>
      );
    default:
      return <span>{action}</span>;
  }
};

// Helper function to render notification with icon and label
const renderNotification = (notification) => {
  if (!notification || notification === 'N/A') {
    return <span className="text-gray-500">N/A</span>;
  }
  
  switch (notification.toLowerCase()) {
    case 'email':
      return (
        <div className="flex items-center">
          <MailIcon className="w-4 h-4 text-blue-500" /> 
          <span className="ml-2">Email</span>
        </div>
      );
    case 'slack':
      return (
        <div className="flex items-center">
          <img src="/icons/slack.png" alt="Slack" className="w-4 h-4" />
          <span className="ml-2">Slack</span>
        </div>
      );
    case 'sms':
      return (
        <div className="flex items-center">
          <Chat sx={{ fontSize: '16px', color: '#10B981' }} /> 
          <span className="ml-2">SMS</span>
        </div>
      );
    case 'front':
      return (
        <div className="flex items-center">
          <img src="/icons/front.png" alt="Front" className="w-4 h-4" />
          <span className="ml-2">Front</span>
        </div>
      );
    default:
      return <span>{notification}</span>;
  }
};

const formatSeconds = (seconds) => {
  if (!Number.isFinite(seconds) || seconds < 0) {
    return "0:00";
  }
  const wholeSeconds = Math.floor(seconds);
  const minutes = Math.floor(wholeSeconds / 60);
  const remainingSeconds = wholeSeconds % 60;
  return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
};

const TranscriptPlayback = ({ call, callId, onRefetchCall }) => {
  const [transcript, setTranscript] = useState([]);
  const [audioUrl, setAudioUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [error, setError] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [hasFetched, setHasFetched] = useState(false);
  const audioRef = useRef(null);
  const transcriptRefs = useRef([]);
  const containerRef = useRef(null);
  const previousActiveIndex = useRef(null);
  const [speedMenuAnchor, setSpeedMenuAnchor] = useState(null);
  const [downloadMenuAnchor, setDownloadMenuAnchor] = useState(null);
  const playbackRates = useMemo(() => [1, 1.25, 1.5, 2, 3], []);

  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 500);
  };

  const fetchTranscript = async () => {
    setIsFetching(true);
    setError(null);

    try {
      if (!call?.transcript_s3) {
        throw new Error("Transcript URL not available");
      }

      // Use simple fetch - presigned URLs should work if CORS is configured on S3 bucket
      const transcriptResponse = await fetch(call.transcript_s3);
      
      if (!transcriptResponse.ok) {
        throw new Error(`Failed to fetch transcript: ${transcriptResponse.status} ${transcriptResponse.statusText}`);
      }
      
      const transcriptData = await transcriptResponse.json();
      setTranscript(Array.isArray(transcriptData) ? transcriptData : []);
      setHasFetched(true);
    } catch (err) {
      // Log for production diagnostics when transcript fetch fails
      console.error("Error fetching transcript:", err);
      // If CORS fails, the error will indicate it
      if (err.message.includes('CORS') || err.message.includes('Failed to fetch')) {
        setError("CORS error: Unable to fetch transcript. The S3 bucket may need CORS configuration, or a backend proxy endpoint may be required.");
      } else {
        setError(`Failed to load transcript: ${err.message}`);
      }
    } finally {
      setIsFetching(false);
    }
  };

  // Auto-load audio when has_recording is true
  useEffect(() => {
    if (!call) return;
    
    const hasRecording = call.has_recording === true;
    
    if (hasRecording && call.s3Object) {
      // Check if s3Object is a valid URL (not 'htp' placeholder and has length)
      const isValidUrl = call.s3Object !== 'htp' && 
                        typeof call.s3Object === 'string' && 
                        call.s3Object.length > 1 &&
                        (call.s3Object.startsWith('http://') || call.s3Object.startsWith('https://'));
      
      if (isValidUrl) {
        setAudioUrl(call.s3Object);
      }
    }
  }, [call]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !audioUrl) return;

    // Reset audio state when URL changes
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);

    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
    };

    const handleLoadedMetadata = () => {
      setDuration(audio.duration);
    };

    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(audio.duration);
    };

    const handleError = (e) => {
      // Log when recording audio fails to load
      console.error("Audio loading error:", e);
      setError("Failed to load audio. Please try again.");
      setIsPlaying(false);
    };

    // Load the audio source
    audio.load();

    audio.addEventListener("timeupdate", handleTimeUpdate);
    audio.addEventListener("loadedmetadata", handleLoadedMetadata);
    audio.addEventListener("play", handlePlay);
    audio.addEventListener("pause", handlePause);
    audio.addEventListener("ended", handleEnded);
    audio.addEventListener("error", handleError);

    return () => {
      audio.removeEventListener("timeupdate", handleTimeUpdate);
      audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
      audio.removeEventListener("play", handlePlay);
      audio.removeEventListener("pause", handlePause);
      audio.removeEventListener("ended", handleEnded);
      audio.removeEventListener("error", handleError);
    };
  }, [audioUrl]);

  useEffect(() => {
    if (audioRef.current && audioUrl) {
      audioRef.current.playbackRate = playbackRate;
    }
  }, [playbackRate, audioUrl]);

  // Reset transcript state when transcript URL changes (but not audio - audio auto-loads)
  useEffect(() => {
    setHasFetched(false);
    setTranscript([]);
    setError(null);
    // Don't reset audioUrl here - it's managed by the audio loading effect
    // Don't reset playback state here - let it persist
  }, [call?.transcript_s3]);

  const activeIndex = useMemo(() => {
    if (!transcript.length) {
      return -1;
    }

    const directMatch = transcript.findIndex(
      (entry) => currentTime >= entry.start && currentTime <= entry.end
    );
    if (directMatch !== -1) {
      return directMatch;
    }

    const lastBefore = transcript.reduce((acc, entry, index) => {
      if (currentTime >= entry.start) {
        return index;
      }
      return acc;
    }, -1);

    if (lastBefore !== -1) {
      return lastBefore;
    }

    return 0;
  }, [transcript, currentTime]);

  useEffect(() => {
    if (
      activeIndex === -1 ||
      activeIndex === previousActiveIndex.current ||
      !containerRef.current
    ) {
      return;
    }

    const activeElement = transcriptRefs.current[activeIndex];
    if (!activeElement) return;

    const container = containerRef.current;
    const paddingTop = 16; // pt-4 padding
    const scrollMargin = 16; // Extra spacing above/below
    
    // Use requestAnimationFrame to ensure DOM is updated
    requestAnimationFrame(() => {
      const containerRect = container.getBoundingClientRect();
      const elementRect = activeElement.getBoundingClientRect();
      
      // Calculate positions relative to container's visible area
      const elementTopRelative = elementRect.top - containerRect.top;
      const elementBottomRelative = elementRect.bottom - containerRect.top;
      const containerHeight = container.clientHeight;
      
      // Define safe visible area
      const safeTop = paddingTop;
      const safeBottom = containerHeight - scrollMargin;
      
      const currentScrollTop = container.scrollTop;
      let newScrollTop = currentScrollTop;
      let needsScroll = false;
      
      // Check if element is cut off at top
      if (elementTopRelative < safeTop) {
        // CRITICAL: If we're at the very top (scrollTop === 0), NEVER scroll up
        // This prevents cutting off the first element
        if (currentScrollTop === 0) {
          needsScroll = false;
        } else {
          // Calculate how much to scroll
          const scrollDelta = safeTop - elementTopRelative;
          newScrollTop = currentScrollTop - scrollDelta;
          
          // ABSOLUTE RULE: Never scroll past 0
          if (newScrollTop < 0) {
            newScrollTop = 0;
          }
          
          needsScroll = Math.abs(newScrollTop - currentScrollTop) > 1;
        }
      }
      // Check if element extends below safe area (but only if top is visible)
      else if (elementTopRelative >= safeTop && elementBottomRelative > safeBottom) {
        // Calculate how much to scroll down
        const scrollDelta = elementBottomRelative - safeBottom;
        newScrollTop = currentScrollTop + scrollDelta;
        
        // Never scroll past 0 (shouldn't happen when scrolling down, but safety check)
        if (newScrollTop < 0) {
          newScrollTop = 0;
        }
        
        needsScroll = Math.abs(newScrollTop - currentScrollTop) > 1;
      }
      
      // Perform scroll if needed
      if (needsScroll) {
        container.scrollTo({
          top: newScrollTop,
          behavior: "smooth",
        });
      }
    });

    previousActiveIndex.current = activeIndex;
  }, [activeIndex]);

  const togglePlayback = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
    } else {
      audio
        .play()
        .catch(() => {
          setError("Playback was prevented by the browser. Try interacting with the page first.");
        });
    }
  };

  const handleSeek = (delta) => {
    const audio = audioRef.current;
    if (!audio) return;
    const targetTime = Math.min(Math.max(audio.currentTime + delta, 0), duration || audio.duration || 0);
    audio.currentTime = targetTime;
    setCurrentTime(targetTime);
  };

  const handleSliderChange = (_, newValue) => {
    const audio = audioRef.current;
    if (!audio) return;

    const value = Array.isArray(newValue) ? newValue[0] : newValue;
    audio.currentTime = value;
    setCurrentTime(value);
  };

  const handleLineClick = (timestamp) => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.currentTime = timestamp;
    setCurrentTime(timestamp);
    audio
      .play()
      .then(() => setIsPlaying(true))
      .catch(() => {
        setError("Playback was prevented by the browser. Try interacting with the page first.");
      });
  };

  const openSpeedMenu = (event) => {
    setSpeedMenuAnchor(event.currentTarget);
  };

  const closeSpeedMenu = () => {
    setSpeedMenuAnchor(null);
  };

  const handlePlaybackRateChange = (rate) => {
    setPlaybackRate(rate);
    if (audioRef.current) {
      audioRef.current.playbackRate = rate;
    }
    closeSpeedMenu();
  };

  const openDownloadMenu = (event) => {
    setDownloadMenuAnchor(event.currentTarget);
  };

  const closeDownloadMenu = () => {
    setDownloadMenuAnchor(null);
  };

  const handleDownload = (type) => {
    if (!transcript.length) {
      closeDownloadMenu();
      return;
    }

    if (type === "json") {
      const blob = new Blob([JSON.stringify(transcript, null, 2)], {
        type: "application/json",
      });
      downloadBlob(blob, "call-transcript.json");
    } else if (type === "pdf") {
      const blob = createPdfBlobFromTranscript(transcript);
      downloadBlob(blob, "call-transcript.pdf");
    }
    closeDownloadMenu();
  };

  // Determine what to show based on call data
  const hasRecording = call?.has_recording === true;
  const hasTranscript = call?.has_transcript === true;
  // Support both transcription_status and transcript_status; handle Dynamo-style .S
  const transcriptionStatusRaw = call?.transcription_status ?? call?.transcript_status;
  const transcriptionStatus =
    typeof transcriptionStatusRaw === "string"
      ? transcriptionStatusRaw
      : transcriptionStatusRaw?.S;

  // Empty state: no recording and no transcript
  if (!hasRecording && !hasTranscript) {
    return (
      <div className="bg-white rounded-[13px] shadow-sm p-4 sm:p-6 flex flex-col w-full 2xl:w-[45%] items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="text-[18px] font-semibold text-gray-900 mb-2">No Recording Available</div>
          <div className="text-[14px] text-gray-500">
            Call recording and transcription were not enabled for this call.
          </div>
        </div>
      </div>
    );
  }

  // Render playback and transcript sections
  return (
    <div className="bg-white rounded-[13px] shadow-sm p-4 sm:p-6 flex flex-col w-full 2xl:w-[45%]">
      {/* Call Playback Section - Show if has_recording is true */}
      {hasRecording && (
        <>
          <div className="flex items-center justify-between mb-4">
            <div className="text-[18px] font-semibold text-gray-900">Call Playback</div>
          </div>

          {audioUrl ? (
        <div className="bg-gray-50 border border-gray-100 rounded-xl p-4 mb-4">
          <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
            <div className="flex items-center gap-2 order-1">
              <button
                type="button"
                onClick={() => handleSeek(-10)}
                className="p-2 rounded-full bg-white border border-gray-200 text-gray-600 hover:border-indigo-200 hover:text-indigo-600 transition-colors"
              >
                <Replay10 fontSize="small" />
              </button>
              <button
                type="button"
                onClick={togglePlayback}
                className="p-3 rounded-full bg-indigo-600 text-white shadow hover:bg-indigo-500 transition-colors"
              >
                {isPlaying ? <Pause fontSize="medium" /> : <PlayArrow fontSize="medium" />}
              </button>
              <button
                type="button"
                onClick={() => handleSeek(10)}
                className="p-2 rounded-full bg-white border border-gray-200 text-gray-600 hover:border-indigo-200 hover:text-indigo-600 transition-colors"
              >
                <Forward10 fontSize="small" />
              </button>
            </div>
            <div className="flex items-center gap-3 order-3 sm:order-2">
              <button
                type="button"
                onClick={openSpeedMenu}
                className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white border border-gray-200 text-gray-600 hover:border-indigo-200 hover:text-indigo-600 transition-colors text-[13px] font-medium"
              >
                <Speed sx={{ fontSize: 18 }} />
                {playbackRate}x
              </button>
              <Menu
                anchorEl={speedMenuAnchor}
                open={Boolean(speedMenuAnchor)}
                onClose={closeSpeedMenu}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
              >
                {playbackRates.map((rate) => (
                  <MenuItem
                    key={rate}
                    selected={rate === playbackRate}
                    onClick={() => handlePlaybackRateChange(rate)}
                  >
                    {rate}x
                  </MenuItem>
                ))}
              </Menu>
            </div>
            <div className="text-[13px] font-medium text-gray-600 order-2 sm:order-3 ml-auto">
              {formatSeconds(currentTime)} <span className="text-gray-400">/</span>{" "}
              {formatSeconds(duration)}
            </div>
          </div>

          <Slider
            color="primary"
            value={duration ? Math.min(currentTime, duration) : 0}
            min={0}
            max={duration || 0}
            step={0.1}
            onChange={handleSliderChange}
            aria-label="Playback progress"
            sx={{
              "& .MuiSlider-rail": { opacity: 0.3 },
              "& .MuiSlider-thumb": {
                width: 14,
                height: 14,
                boxShadow: "0px 2px 6px rgba(0,0,0,0.15)",
              },
            }}
          />
          <audio 
            ref={audioRef} 
            src={audioUrl || undefined} 
            preload="metadata" 
            className="hidden"
          />
        </div>
      ) : (
        <div className="bg-gray-50 border border-gray-100 rounded-xl p-4 mb-4">
          <div className="flex items-center justify-center py-4 text-gray-500 text-[14px]">
            <CircularProgress size={24} color="primary" sx={{ mr: 2 }} />
            Loading recording...
          </div>
        </div>
      )}
        </>
      )}

      {/* Transcript Section */}
      <div className="mt-6">
        <div className="text-[16px] font-semibold text-gray-900 mb-3">Transcript</div>

        {/* If transcript is fetched and available, show it */}
        {hasFetched && transcript.length > 0 && (
          <>
            <div className="flex items-center justify-end mb-3">
              <button
                type="button"
                onClick={openDownloadMenu}
                className="flex items-center gap-2 px-3 py-2 rounded-lg border bg-white border-gray-200 text-gray-600 hover:border-indigo-200 hover:text-indigo-600 text-[13px] font-medium transition-colors"
              >
                <Download sx={{ fontSize: 18 }} />
                Download
              </button>
              <Menu
                anchorEl={downloadMenuAnchor}
                open={Boolean(downloadMenuAnchor)}
                onClose={closeDownloadMenu}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
              >
                <MenuItem onClick={() => handleDownload("pdf")}>Download PDF</MenuItem>
                <MenuItem onClick={() => handleDownload("json")}>Download JSON</MenuItem>
              </Menu>
            </div>

            <div
              ref={containerRef}
              className="flex-1 min-h-[280px] max-h-[460px] overflow-y-auto pr-2 pt-4 space-y-2"
            >
              {transcript.map((entry, index) => {
                const isActive = index === activeIndex;
                return (
                  <button
                    key={`${entry.speaker}-${entry.start}-${index}`}
                    ref={(element) => {
                      transcriptRefs.current[index] = element;
                    }}
                    onClick={() => handleLineClick(entry.start)}
                    className={`w-full text-left rounded-xl border px-4 py-3 transition-colors ${
                      isActive
                        ? "bg-indigo-50 border-indigo-200 text-indigo-700 shadow-sm"
                        : "bg-white border-gray-100 hover:border-indigo-200 hover:bg-indigo-50/60 text-gray-700"
                    }`}
                  >
                    <div className="flex items-center justify-between text-[12px] font-medium mb-1">
                      <span className={isActive ? "text-indigo-600" : "text-gray-500"}>
                        {entry.speaker}
                      </span>
                      <span className={isActive ? "text-indigo-500" : "text-gray-400"}>
                        {formatSeconds(entry.start)}
                      </span>
                    </div>
                    <div className="text-[14px] leading-relaxed">{entry.text}</div>
                  </button>
                );
              })}
            </div>
          </>
        )}

        {/* If transcript is being fetched */}
        {isFetching && (
          <div className="flex items-center justify-center py-8 text-gray-500 text-[14px]">
            <CircularProgress size={24} color="primary" />
            <span className="ml-2">Loading transcript...</span>
          </div>
        )}

        {/* If there's an error fetching transcript */}
        {!isFetching && error && (
          <div className="flex items-center justify-center py-8 text-red-500 text-[14px] text-center">
            {error}
          </div>
        )}

        {/* Conditional states for transcript */}
        {!isFetching && !error && !hasFetched && (
          <>
            {/* Priority 1: If has_transcript is true AND transcript_s3 exists, show fetch button */}
            {hasTranscript && call?.transcript_s3 ? (
              <div className="flex flex-col items-center justify-center py-8">
                <div className="text-[14px] text-gray-500 mb-4">
                  Click the button below to load the transcript.
                </div>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={fetchTranscript}
                  disabled={isFetching}
                >
                  Fetch Transcript
                </Button>
              </div>
            ) : transcriptionStatus === "pending" ? (
              /* Priority 2: transcription_status is "pending" */
              <div className="flex flex-col items-center justify-center py-8">
                <CircularProgress size={40} color="primary" sx={{ mb: 2 }} />
                <div className="text-[14px] text-gray-500 mb-4 text-center">
                  The transcript is still being processed and should be available in a few moments.
                </div>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={onRefetchCall}
                >
                  Reload
                </Button>
              </div>
            ) : transcriptionStatus === "failed" ? (
              /* Priority 3: transcription_status is "failed" */
              <div className="flex flex-col items-center justify-center py-8">
                <div className="text-[14px] text-gray-500 mb-4 text-center">
                  Transcription failed for this call. If you need assistance, please contact support.
                </div>
                <a
                  href={`mailto:hello@twilanswer.com?subject=Failed Transcript Generation&body=Call ID: ${callId}`}
                  className="text-indigo-600 hover:text-indigo-700 underline text-[14px]"
                >
                  Contact Support
                </a>
              </div>
            ) : (
              /* Priority 4: transcription_status missing or "disabled" – transcript was not requested */
              <div className="flex items-center justify-center py-8 text-gray-500 text-[14px]">
                Transcription was not enabled for this call.
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const CallDetails = ({ conversation }) => {
  const [err_msg, setErrorMsg] = useState(null);
  const navigate = useNavigate();
  const [route, setRoute] = useState({
    route_name: "N/A",
  });
  const [loader, setLoader] = useState(false);
  const [call, setCall] = useState(null);

  const { call_id } = useParams();
  /**
   * Converts call_id from milliseconds to seconds if needed. 
   * We had a bug that stored some call ids in ms. We backfilled the db to ensure consistency, but we need this function for backwards compatibility if anyone has a link for a call id based in ms 
   * Call IDs are in format: {timestamp}-{4 random digits}
   * If the timestamp part is 13 digits (milliseconds), it will be converted to seconds (10 digits)
   * to maintain consistency with the backend which expects seconds.
   * 
   * Examples:
   * - "1738957083850-3955" (ms) → "1738957083-3955" (seconds)
   * - "1740490496-8709" (seconds) → "1740490496-8709" (unchanged)
   * 
   * @param {string} callId - The call ID from the URL parameter (format: timestamp-4digits)
   * @returns {string} - The call ID with timestamp in seconds format
   */
  const normalizeCallIdToSeconds = (callId) => {
    if (!callId) return callId;
    
    // Split by hyphen to separate timestamp and random digits
    const parts = callId.split('-');
    
    // If format is incorrect (no hyphen or more than one hyphen), return as-is
    if (parts.length !== 2) {
      return callId;
    }
    
    const [timestampPart, randomDigits] = parts;
    
    // If timestamp part is 13 digits (milliseconds), convert to seconds
    if (timestampPart.length === 13) {
      const msValue = parseInt(timestampPart, 10);
      if (!isNaN(msValue)) {
        const secondsTimestamp = Math.floor(msValue / 1000).toString();
        return `${secondsTimestamp}-${randomDigits}`;
      }
    }
    
    // Otherwise, return as-is (already in seconds or invalid format)
    return callId;
  };

  const getRoute = async () => {
    //alert(call?.route)
    try {
      let route = await axios.post(
        process.env.REACT_APP_BACKEND_URL + "route/get-route",
        { route_id: call?.route, org_id: JSON.parse(localStorage.getItem("organization")).orgId.S }
      );

      setLoader(false);

      let r = route?.data.route;

      setRoute({
        route_id: r.routeId,
        route_name: r.name,
        configured_phone: r.configured_phone,
        organization_id: r.org_id,
        early_time: r.earlyTime,
        late_time: r.lateTime,
        primaryAction: r.primaryAction ?? r.primary_action,
        recording_id: r.primaryRecording,
        primary_number: r.primaryNumber,
        off_hours_number: r.offHoursNumber,
        primary_channel: r.primaryChannel,
        offHoursAction: r.offHoursAction ?? r.offhours_action,
        off_hours_recording: r.offHoursRecording,
        off_hours_number: r.offHoursNumber,
        off_hours_channel: r.offHoursChannel,
        active_friday: r.calendar.fri.BOOL,
        active_monday: r.calendar.mon.BOOL,
        active_saturday: r.calendar.sat.BOOL,
        active_sunday: r.calendar.sun.BOOL,
        active_thursday: r.calendar.thur.BOOL,
        active_tuesday: r.calendar.tue.BOOL,
        active_wednesday: r.calendar.wed.BOOL,
      });
    } catch (error) {
      // Log for production diagnostics when get route fails
      console.error("CallDetails: get route failed", error.response?.data || error.message || error);
      if (error.response && error.response.data) {
        if (
          error.response.data.message ===
          `Route with route_id ${call?.route} not found`
        ) {
          setRoute({
            ...route,
            route_name: "Deleted Route",
            route_id: "deleted",
          });
        } else {
          setErrorMsg(error.response.data.message);
        }
      }
      setLoader(false);
    }
  };

  const getCall = async () => {
    setLoader(true);
    try {
      // Convert call_id from milliseconds to seconds if needed
      const normalizedCallId = normalizeCallIdToSeconds(call_id);
      
      let call_ = await axios.post(
        process.env.REACT_APP_BACKEND_URL + "call/get-call",
        {
          call_id: normalizedCallId,
          organization_id: JSON.parse(localStorage.getItem('organization')).orgId.S,
        }
      );
      let call__ = call_.data.call;

      setCall(call__);

      setLoader(false);
    } catch (error) {
      // Log for production diagnostics when get call fails
      console.error("CallDetails: get call failed", error.response?.data || error.message || error);
      if (error.response && error.response.data) {
        if (error.response.data.message === "001") {
          setChangeOrg(true);
        } else {
          const status = error?.response?.status;
          const apiError = error?.response?.data?.error;
          const apiMessage = error?.response?.data?.message;

          if (
            status === 401 &&
            typeof apiError === "string" &&
            apiError.toLowerCase().includes("authorization header")
          ) {
            setErrorMsg(
              "We ran into an authentication issue and can’t verify your session. Please log out and log back in."
            );
          } else {
            setErrorMsg([apiError, apiMessage].filter(Boolean).join(" "));
          }
        }
      }
      setLoader(false);
    }
  };

  useEffect(() => {
    getCall();
  }, [conversation]);

  const [change_org, setChangeOrg] = useState(false);
  useEffect(() => {
    if (call?.route) {
      getRoute();
    }
    if (call) {
      if (
        call.organizationId !==
        JSON.parse(localStorage.getItem("organization")).orgId.S
      ) {
        setChangeOrg(false);
      }
    }
  }, [call]);

  const [user, setUser] = useState(JSON.parse(localStorage.getItem("user")));
  const [orgs, setOrgs] = useState();

  useEffect(() => {
    const loadOrgs = async () => {
      const { getAllOrgs } = await import('../../utils/getAllOrgs');
      try {
        await getAllOrgs(setOrgs, setLoader);
      } catch (error) {
        // Log for production diagnostics when org list fails to load
        console.error('[CallDetails] Error loading organizations:', error);
      }
    };
    loadOrgs();
  }, []);

  const changeOrganization = async (org_id) => {
    try {
      const { fetchAndSetOrganization } = await import("../../utils/organizationService");
      await fetchAndSetOrganization(org_id);
      window.location.reload();
    } catch (error) {
      // Log for production diagnostics when change organization fails
      console.error("CallDetails: change organization failed", error.response?.data || error.message || error);
      if (error.response && error.response.data) {
        setErrorMsg(error.response.data.message);
      }
      setLoader(false);
    }
  };

  return (
    <div className="pt-7 px-2 sm:px-4">
      {/* <ChangeOrgModalNew open={change_org} setOpen={setChangeOrg} /> */}
      {!change_org && (
        <>
          <div className="flex items-center space-x-3">
            <button
              onClick={() => navigate("/dashboard/")}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
            >
              <ArrowLeft className="w-[18px]" />
            </button>
            <div>
              <div className="lg:text-[24px] text-[18px] font-bold">
                Call Details
              </div>
              <div className="text-gray-500 text-[13px] mb-4">
                Call ID: {call?.callId?.S || call?.callId || call_id}
              </div>
            </div>
          </div>

          {err_msg && (
            <div className="text-center text-red-500 lg:text-[13px] text-[12px] mt-2 font-semibold mb-3">
              <ErrorOutline sx={{ fontSize: "" }} /> {err_msg}
            </div>
          )}

          {loader && (
            <CircularProgress
              size={"1.2rem"}
              color="primary"
              sx={{ animationDuration: "0.7s" }}
            />
          )}
        </>
      )}

      {!change_org ? (
        <div className="my-5 flex flex-col 2xl:flex-row gap-6">
          <div className="bg-white rounded-[13px] w-full 2xl:w-[55%] p-4 sm:p-8 shadow-sm">
            {/* Call Information Section */}
            <div className="mb-6">
              <div className="text-[18px] font-semibold mb-4">Call Information</div>
              <div className="space-y-3">
                <div className="flex items-center text-[15px]">
                  <AccessTime className="mr-2 text-gray-500" fontSize="small" />
                  <span className="font-medium text-gray-600 w-28">Time</span>
                  <span className="ml-2">{call && call.startTime ? `${formatTimestampForZone(call?.startTime)}` : "N/A"}</span>
                </div>
                <div className="flex items-center text-[15px]">
                  <Phone className="mr-2 text-gray-500" fontSize="small" />
                  <span className="font-medium text-gray-600 w-28">To Number</span>
                  <span className="ml-2">{formatPhoneNumber(call?.toNumber) || "N/A"}</span>
                </div>
                <div className="flex items-center text-[15px]">
                  <ArrowForward className="mr-2 text-gray-500" fontSize="small" />
                  <span className="font-medium text-gray-600 w-28">Route</span>
                  <span className="ml-2 hover:underline cursor-pointer hover:text-blue-700"
                    onClick={() => { navigate('/dashboard/call-route/' + call?.route) }}>
                    {route?.route_name || "N/A"}
                  </span>
                </div>
                <div className="flex items-center text-[15px]">
                  <Phone className="mr-2 text-gray-500" fontSize="small" />
                  <span className="font-medium text-gray-600 w-28">From</span>
                  <span className="ml-2">{formatPhoneNumber(call?.fromNumber) || "N/A"}</span>
                </div>
                {(call?.call_user?.S || call?.call_user) && (
                  <div className="flex items-center text-[15px]">
                    <Person className="mr-2 text-gray-500" fontSize="small" />
                    <span className="font-medium text-gray-600 w-28">Call User</span>
                    <span className="ml-2">{call?.call_user?.S || call?.call_user || "N/A"}</span>
                  </div>
                )}
                {call?.duration && (
                  <div className="flex items-center text-[15px]">
                    <AccessTime className="mr-2 text-gray-500" fontSize="small" />
                    <span className="font-medium text-gray-600 w-28">Duration</span>
                    <span className="ml-2">{call.duration}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Action Details Section */}
            <div className="mb-6">
              <div className="text-[18px] font-semibold mb-4">Action Details</div>
              <div className="space-y-3">
                <div className="flex items-center text-[15px]">
                  <ArrowForward className="mr-2 text-gray-500" fontSize="small" />
                  <span className="font-medium text-gray-600 w-28">Action</span>
                  <div className="ml-2">{renderAction(call?.action)}</div>
                </div>
                <div className="flex items-center text-[15px]">
                  <ArrowForward className="mr-2 text-gray-500" fontSize="small" />
                  <span className="font-medium text-gray-600 w-28">Action Details</span>
                  <Tooltip 
                    title={call?.action_details && call?.action_details !== "N/A" ? call.action_details : ""}
                    arrow
                    placement="top"
                  >
                    <span className="ml-2 cursor-help">{getActionDetailsLabel(call?.action_details)}</span>
                  </Tooltip>
                </div>
                <div className="flex items-center text-[15px]">
                  <Email className="mr-2 text-gray-500" fontSize="small" />
                  <span className="font-medium text-gray-600 w-28">Notification</span>
                  <div className="ml-2">{renderNotification(call?.notification)}</div>
                </div>
                <div className="flex items-center text-[15px]">
                  <Email className="mr-2 text-gray-500" fontSize="small" />
                  <span className="font-medium text-gray-600 w-28">Notification Details</span>
                  <span className="ml-2">{call?.notificationDetails || "N/A"}</span>
                </div>
              </div>
            </div>

            {/* Voicemail Section */}
            <div className="mb-2">
              <div className="text-[18px] font-semibold mb-4 flex items-center">
                <Voicemail className="mr-2 text-gray-500" fontSize="medium" />
                Voicemail
              </div>
              {call?.has_vm === true && call?.s3Object !== 'htp' && call?.s3Object?.length > 1 ? (
                <AudioPlayer url={call?.s3Object} />
              ) : (
                <div className="text-[15px] text-gray-500 bg-gray-50 rounded-lg p-4">This call does not have a voicemail</div>
              )}
              {/* Return Call Button */}
              {(call?.fromNumber || call?.toNumber) && (
                <div className="mt-4 flex justify-start">
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<img src="/icons/phone_white.png" className="w-[20px] h-[20px]" alt="Return Call" />}
                    onClick={() => {
                      const isOutbound = call?.action === 'Outbound Call';
                      const recipient = isOutbound ? call?.toNumber : call?.fromNumber;
                      navigate(`/dashboard/dialer?recipient=${encodeURIComponent(recipient)}`);
                    }}
                    sx={{
                      borderRadius: '7px',
                      fontSize: { lg: '17px', xs: '15px' },
                      px: { lg: '36px', xs: '20px' },
                      py: { lg: '4px', xs: '10px' },
                      boxShadow: 1,
                      width: { xs: '100%', sm: 'auto' },
                      ':hover': {
                        boxShadow: 3,
                      },
                    }}
                  >
                    {call?.action === 'Outbound Call' ? 'Redial' : 'Return Call'}
                  </Button>
                </div>
              )}
            </div>

            {/* Transcription Section (if available) */}
            {call?.has_vm && (
              <div className="mt-4">
                <div className="text-[16px] font-semibold mb-1">Transcription</div>
                <div className="text-[14px]">
                  {call?.vmTranscription || (<div className="text-[11px]">Transcription unavailable</div>)}
                </div>
              </div>
            )}
          </div>
          <TranscriptPlayback call={call} callId={call_id} onRefetchCall={getCall} />
        </div>
      ) : (
        <div>
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center">
              <button
                onClick={() => navigate("/dashboard/")}
                className="mr-4 p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
              >
                <ArrowLeft size={20} />
              </button>
              <div>
                <h1 className="text-2xl font-semibold text-gray-900">Call Details</h1>
              </div>
            </div>
          </div>

          {/* Error Card */}
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <div className="p-6">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-gray-800">
                  <p className="mb-2">
                    Oops! We couldn't find a record for call{" "}
                    <span className="font-mono font-semibold text-gray-900">{call_id}</span> in{" "}
                    <span className="font-semibold">
                      {JSON.parse(localStorage.getItem("organization"))?.organization_name?.S || "Twilanswer"}
                    </span>
                    . Is it possible the URL is incorrect or that you mean to be logged in as a different organization?
                  </p>
                  <p>
                    If you need help, please{" "}
                    <a
                      href="mailto:hello@twilanswer.com"
                      className="text-indigo-600 hover:text-indigo-700 underline"
                    >
                      Contact Support
                    </a>
                    .
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CallDetails;
